/**
 * Auth Dialog Integration Utility
 * Handles the flow when user chooses "Guest" or "Register" from auth dialog
 *
 * Flow:
 * 1. User clicks wishlist/cart button
 * 2. Auth dialog shows (if not logged in)
 * 3. User chooses: Continue as Guest OR Create Account
 * 4. Backend returns: { uid, ... }
 * 5. This utility saves the ID and updates Redux
 */

import type { NavigateFunction } from "react-router-dom";
import { showAuthVerificationDialog } from "./authDialogUtils";
import {
  handleGuestLoginSuccess,
  handleUserLoginSuccess,
  readAuthFromCookies,
} from "./authUtils";
import { clearRedirectAfterLogin } from "./redirectUtils";
import type { AppDispatch } from "../store/store";
import { openAuthModal } from "../store/uiSlice";
import type { UserInfo } from "../store/authSlice";

export interface AuthDialogHandlers {
  onAuthSuccess?: () => void;
}

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_BASE_URL ||
  ""
).replace(/\/+$/, "");

// Prevents rapid re-calls to the guest auth endpoint while a request is in flight
let guestAuthInFlight = false;

export const handleAuthDialog = (
  dispatch: AppDispatch,
  navigate: NavigateFunction,
  handlers?: AuthDialogHandlers
) => {
  showAuthVerificationDialog({
    onContinueAsGuest: async () => {
      if (guestAuthInFlight) return;
      guestAuthInFlight = true;
      try {
        const auth = readAuthFromCookies();

        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (auth?.jwt) headers["Authorization"] = `Bearer ${auth.jwt}`;
        if (auth?.pksoft) headers["x-auth"] = auth.pksoft;

        const response = await fetch(
          `${API_BASE_URL}/api/pk/Customer/guest/auth`,
          { method: "POST", headers, body: JSON.stringify({}) }
        );

        const data = await response.json();

        if (data.uid) {
          handleGuestLoginSuccess(data.uid, dispatch);
          clearRedirectAfterLogin();
          handlers?.onAuthSuccess?.();
        } else {
          console.error("[DIALOG] No uid in response");
        }
      } catch (error) {
        console.error("[DIALOG] Guest login error:", error);
      } finally {
        guestAuthInFlight = false;
      }
    },

    // Login/register open as a modal over the current page, so there is no
    // route to come back to.
    onUserRegistration: () => {
      dispatch(openAuthModal({ mode: "register" }));
    },

    onAlreadyHaveAccount: () => {
      dispatch(openAuthModal({ mode: "login" }));
    },
  });
};

export const handleRegistrationSuccess = (
  userId: string,
  userInfo: UserInfo,
  dispatch: AppDispatch
) => {
  handleUserLoginSuccess(userId, userInfo, dispatch);
};

export const handleLoginSuccess = (
  userId: string,
  userInfo: UserInfo,
  dispatch: AppDispatch
) => {
  handleUserLoginSuccess(userId, userInfo, dispatch);
};

const authDialogIntegration = {
  handleAuthDialog,
  handleRegistrationSuccess,
  handleLoginSuccess,
};

export default authDialogIntegration;
